import React, { useState, useEffect, useRef } from 'react';
import { X, Bug, Send, CheckCircle, AlertCircle, Leaf } from 'lucide-react';
import emailjs from 'emailjs-com';
import { useTheme } from './ThemeContext';

const BugReportButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { colors } = useTheme(); 

  const [bugReport, setBugReport] = useState({
    type: 'bug',
    description: '',
    page: window.location.pathname,
    email: ''
  });
  const [status, setStatus] = useState({ severity: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  // DRAGGING STATE
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  const isMobile = window.innerWidth <= 768; 

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init('gHDv2j1boFBdQTfm3');
  }, []);

  // DRAG HANDLERS
  const handleMouseDown = (e) => {
    if (isOpen) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleTouchStart = (e) => {
    if (isOpen) return;
    
    const touch = e.touches[0];
    const rect = buttonRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
  };

  // DRAG EFFECT
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 140);
      const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 44);
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 140);
      const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 44);
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ severity: '', message: '' });

    // Prepare the data for EmailJS
    const emailData = {
      email: bugReport.email || 'anonymous@user.com',
      subject: `Bug Report: ${bugReport.type.toUpperCase()} - ${bugReport.page}`,
      message: `
      🌿FROM OAK🌿
Bug Report Details:
-------------------
Issue Type: ${bugReport.type}
Current Page: ${bugReport.page}
User Email: ${bugReport.email || 'Not provided'}

Description:
${bugReport.description}
🌿FROM OAK🌿
-------------------
Submitted at: ${new Date().toLocaleString()}
User Agent: ${navigator.userAgent}
      `.trim()
    };

    // Send email using the same service and template as your contact form
    emailjs.send('get_email', 'tem', emailData)
      .then(
        (result) => {
          setStatus({
            severity: 'success',
            message: 'Bug report submitted successfully! Thank you for helping us improve.'
          });
          setBugReport({ 
            type: 'bug', 
            description: '', 
            page: window.location.pathname, 
            email: '' 
          });
          setLoading(false);
          
          // Auto-close after 3 seconds on success
          setTimeout(() => {
            setIsOpen(false);
            setStatus({ severity: '', message: '' });
          }, 3000);
        },
        (error) => {
          console.error('EmailJS Error:', error);
          setStatus({
            severity: 'error',
            message: 'Unable to send bug report. Please try again or contact us directly.'
          });
          setLoading(false);
        }
      );
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setStatus({ severity: '', message: '' });
    setBugReport({ 
      type: 'bug', 
      description: '', 
      page: window.location.pathname, 
      email: '' 
    });
    setFocusedField('');
  };

  const handleInputChange = (field, value) => {
    setBugReport(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div 
      ref={buttonRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 300000,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
      `}</style>

      {/* Main Bug Report Button - Updated to match your design */}
      <button 
  style={{
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 20px',
    borderRadius: '50px',
    cursor: isDragging ? 'grabbing' : 'pointer',
    boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'inherit',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: isMobile ? '48px' : '140px',
    justifyContent: 'center',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)'
  }}
  onMouseDown={handleMouseDown}
  onTouchStart={handleTouchStart}
  onClick={(e) => {
    if (!isDragging) {
      setIsOpen(!isOpen);
    }
  }}
  onMouseEnter={(e) => {
    if (!isDragging) {
      e.target.style.background = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
      e.target.style.transform = 'scale(1.05) translateY(-2px)';
      e.target.style.boxShadow = '0 12px 30px rgba(139, 92, 246, 0.5)';
    }
  }}
  onMouseLeave={(e) => {
    e.target.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
    e.target.style.transform = 'scale(1) translateY(0)';
    e.target.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.4)';
  }}
  aria-label="Report a bug or issue"
>
        <Bug size={16} />
        {!isMobile && <span>Report Issue</span>}
      </button>
      
      {/* Bug Report Modal - Updated to match your design */}
      {isOpen && (
        <>
          {/* Backdrop - Darker like your login page */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              backdropFilter: 'blur(10px)',
              zIndex: 9998
            }}
            onClick={handleCloseModal}
          />
          
          {/* Modal Content - Dark theme matching your login */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            color: 'white',
            animation: 'slideIn 0.3s ease-out'
          }}>
            {/* Header - Updated styling */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '32px 32px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 2s infinite'
                }}>
                  <Bug size={24} color="white" />
                </div>
                <div>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '24px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #ffffff 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: 'inherit'
                  }}>Report an Issue</h3>
                  <p style={{
                    margin: 0,
                    color: '#888',
                    fontSize: '14px'
                  }}>Help us improve the community</p>
                </div>
              </div>
              <button
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  color: '#888'
                }}
                onClick={handleCloseModal}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#888';
                }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form - Updated styling to match your login form */}
            <form onSubmit={handleSubmit} style={{ padding: '24px 32px 32px' }}>
              {/* Status Message */}
              {status.message && (
                <div style={{
                  marginBottom: '24px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: `1px solid ${status.severity === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  background: status.severity === 'success' 
                    ? 'rgba(34, 197, 94, 0.1)' 
                    : 'rgba(239, 68, 68, 0.1)',
                  color: status.severity === 'success' ? '#86efac' : '#fca5a5',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {status.severity === 'success' ? 
                    <CheckCircle size={16} /> : 
                    <AlertCircle size={16} />
                  }
                  {status.message}
                </div>
              )}

              {/* Issue Type */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5',
                  fontFamily: 'inherit'
                }}>Issue Type</label>
                <select
                  value={bugReport.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  onFocus={() => setFocusedField('type')}
                  onBlur={() => setFocusedField('')}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: `2px solid ${focusedField === 'type' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                    borderRadius: '16px',
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                >
                  <option value="bug" style={{ background: colors.bgTertiary, color: 'white' }}>🐛 Bug/Error</option>
                  <option value="feature" style={{ background: colors.bgTertiary, color: 'white' }}>💡 Feature Request</option>
                  <option value="improvement" style={{ background: colors.bgTertiary, color: 'white' }}>⚡ Improvement</option>
                  <option value="other" style={{ background: colors.bgTertiary, color: 'white' }}>❓ Other</option>
                </select>
              </div>

              {/* Current Page */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5',
                  fontFamily: 'inherit'
                }}>Current Page</label>
                <input
                  type="text"
                  value={bugReport.page}
                  onChange={(e) => handleInputChange('page', e.target.value)}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    color: '#888',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Email (Optional) */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5',
                  fontFamily: 'inherit'
                }}>Email (Optional)</label>
                <input
                  type="email"
                  value={bugReport.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField('')}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: `2px solid ${focusedField === 'email' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                    borderRadius: '16px',
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
                <small style={{
                  display: 'block',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#888',
                  fontStyle: 'italic'
                }}>We'll only use this to follow up if needed</small>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5',
                  fontFamily: 'inherit'
                }}>Description *</label>
                <textarea
                  value={bugReport.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  onFocus={() => setFocusedField('description')}
                  onBlur={() => setFocusedField('')}
                  placeholder="Please describe the issue you encountered or feature you'd like to see..."
                  rows={4}
                  required
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: `2px solid ${focusedField === 'description' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                    borderRadius: '16px',
                    color: 'white',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    minHeight: '120px',
                    lineHeight: '1.5',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Submit Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '16px 24px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: 'inherit',
                    minWidth: '120px',
                    background: 'transparent',
                    color: '#888',
                    border: '2px solid #333'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#666';
                    e.target.style.color = '#bbb';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#333';
                    e.target.style.color = '#888';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!bugReport.description.trim() || loading}
                  style={{
                    padding: '16px 24px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: (bugReport.description.trim() && !loading) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                    fontFamily: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '160px',
                    justifyContent: 'center',
                    background: (!bugReport.description.trim() || loading)
                      ? '#333'
                      : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: (!bugReport.description.trim() || loading) ? '#666' : 'white',
                    border: 'none',
                    opacity: (!bugReport.description.trim() || loading) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (bugReport.description.trim() && !loading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {loading && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {!loading && <Send size={16} />}
                  {loading ? 'Sending...' : 'Submit Report'}
                </button>
              </div>
            </form>

            {/* Small branding element */}
            <div style={{
              padding: '16px 32px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: 0.6
            }}>
              <Leaf size={16} style={{ color: '#00d4aa' }} />
              <span style={{ fontSize: '12px', color: '#888' }}>
                Help us grow a better community
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BugReportButton;